import { type LoginInput, loginSchema } from '@hm/contracts'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { Button } from '~/components/ui/button'
import { Field, TextInput } from '~/components/ui/field'
import { FormError } from '~/components/ui/primitives'
import { ApiRequestError, api, messageOf } from '~/lib/api'
import { useT } from '~/lib/i18n'
import { SESSION_KEY } from '~/lib/session'
import { AuthLayout } from './auth-layout'

export function LoginPage() {
  const t = useT()
  const queryClient = useQueryClient()
  const form = useForm<LoginInput>({
    resolver: zodResolver(loginSchema),
    defaultValues: { phone: '', password: '' },
  })

  const login = useMutation({
    mutationFn: (input: LoginInput) => api.post('/api/auth/login', input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: SESSION_KEY }),
    onError: (error) => {
      if (error instanceof ApiRequestError) {
        for (const field of error.fields) {
          form.setError(field.path as keyof LoginInput, { message: field.message })
        }
      }
    },
  })

  // A wrong password is reported on the form, not per-field: the server
  // deliberately cannot say which half was wrong (see core/services/auth.ts).
  const formError =
    login.error && (!(login.error instanceof ApiRequestError) || login.error.fields.length === 0)
      ? messageOf(login.error)
      : null

  return (
    <AuthLayout title={t('login.sign_in')} subtitle={t('login.use_the_mobile_number_your_account')}>
      <form
        className="space-y-4"
        onSubmit={form.handleSubmit((values) => login.mutate(values))}
        noValidate
      >
        <Field
          label={t('common.mobile_number')}
          hint={t('login.10_digits_starting_with_9')}
          error={form.formState.errors.phone?.message}
          required
        >
          {(id, describedBy) => (
            <TextInput
              id={id}
              aria-describedby={describedBy}
              type="tel"
              inputMode="numeric"
              accept="phone"
              autoComplete="username"
              placeholder="98-1234-5678"
              prefix="+977"
              autoFocus
              invalid={Boolean(form.formState.errors.phone)}
              {...form.register('phone')}
            />
          )}
        </Field>

        <Field
          label={t('common.password')}
          error={form.formState.errors.password?.message}
          required
        >
          {(id, describedBy) => (
            <TextInput
              id={id}
              aria-describedby={describedBy}
              type="password"
              autoComplete="current-password"
              invalid={Boolean(form.formState.errors.password)}
              {...form.register('password')}
            />
          )}
        </Field>

        {formError ? <FormError>{formError}</FormError> : null}

        <Button type="submit" size="lg" full loading={login.isPending}>
          {t('login.sign_in')}
        </Button>

        <p className="rounded-control border border-border bg-surface-muted px-3.5 py-3 text-center text-xs text-ink-muted">
          {t('login.forgotten_your_password_ask_the_office')}
        </p>
      </form>
    </AuthLayout>
  )
}
